import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import * as SmartySDK from 'smartystreets-javascript-sdk';
import * as sdkUtils from 'smartystreets-javascript-sdk-utils';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { STATE_LIST, parseFootNotes } from '../lib/addressUtils';

const useStyles = makeStyles((theme) => ({
  suggestionTitle: {
    color: theme?.palette?.success?.main,
  },
  suggestionAnalysis: {
    color: theme?.palette?.warning?.main,
  },
  suggestion: {
    padding: '3px 3px',
    color: theme?.palette?.text?.main,
  },
}));

const SmartyAddressField = ({
  values,
  onChange,
  variant,
  streetLabel,
  // additionalLabel,
  cityLabel,
  stateLabel,
  zipcodeLabel,
  countryLabel,
  countries,
}) => {
  const classes = useStyles();
  const [suggestions, setSuggestions] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [suggestion, setSuggestion] = useState(null);
  const [validationAnalysis, setValidationAnalysis] = useState([]);
  const [lookupTimeout, setLookupTimeout] = useState(null);
  const [validateTimeout, setValidateTimeout] = useState(null);
  const [touched, setTouched] = useState(false);
  const defaultCountryId = useMemo(() => {
    const ids = Object.keys(countries);
    return ids.find((cid) => countries[cid].toLowerCase() === 'united states');
  }, [countries]);

  const countryOptions = useMemo(() => {
    const ids = Object.keys(countries);
    return ids.map((cid) => ({ id: cid, name: countries[cid] }));
  }, [countries]);
  const [address, setAddress] = useState({
    street1: '',
    street2: '',
    city: '',
    stateOrProvince: '',
    postalCode: '',
    countryId: defaultCountryId,
    isValid: false,
  });

  const SmartyCore = SmartySDK.core;
  const websiteKey = `${process.env.SMARTY_KEY}`; // Your website key here
  const smartySharedCredentials = new SmartyCore.SharedCredentials(websiteKey);
  const AutocompleteLookup = SmartySDK.usAutocompletePro.Lookup;
  const autoCompleteClientBuilder = new SmartyCore.ClientBuilder(
    smartySharedCredentials,
  ).withLicenses(['us-autocomplete-pro-cloud']);
  const usStreetClientBuilder = new SmartyCore.ClientBuilder(
    smartySharedCredentials,
  ).withLicenses(['us-core-cloud']);

  const autoCompleteClient = autoCompleteClientBuilder.buildUsAutocompleteProClient();
  const usStreetClient = usStreetClientBuilder.buildUsStreetApiClient();
  const updateStateFromValidatedUsAddress = (
    response,
    isAutocomplete = false,
    addr,
  ) => {
    const lookup = response.lookups[0];
    const isValid = sdkUtils.isValid(lookup);
    const isAmbiguous = sdkUtils.isAmbiguous(lookup);
    const isMissingSecondary = sdkUtils.isMissingSecondary(lookup);
    const newState = {
      error: '',
    };
    setSuggestion(null);
    setValidationAnalysis([]);
    if (!isValid) {
      newState.error = 'The address is invalid.';
      setAddress({ ...addr, isValid: false });
    } else if (isAmbiguous) {
      newState.error = 'The address is ambiguous.';
    } else if (isMissingSecondary && !isAutocomplete) {
      newState.error = 'The address is missing a secondary number.';
    } else if (isValid) {
      const {
        deliveryLine1,
        // deliveryLine2,
        components: {
          cityName,
          state,
          zipCode,
          plus4Code,
        },
        metadata: {
          latitude,
          longitude,
        },
        analysis: {
          footnotes,
        },
      } = lookup.result[0];
      const responseAnalysis = parseFootNotes(footnotes);
      const respAddress = {
        street1: deliveryLine1,
        street2: '', // deliveryLine2,
        city: cityName,
        stateOrProvince: state,
        postalCode: zipCode,
        postalAdditional: plus4Code,
        addressLat: latitude,
        addressLong: longitude,
        cityLat: latitude,
        cityLong: longitude,
        isValid,
        // isGeocoded: !!latitude && !!longitude,
        countryId: defaultCountryId,
      };
      setValidationAnalysis(responseAnalysis);
      if (responseAnalysis.length === 0) {
        setAddress(respAddress);
      } else {
        setSuggestion(respAddress);
      }
      clearTimeout(validateTimeout);
      setValidateTimeout(null);
    }

    setErrorMessage(newState.error);
  };

  const validateUsAddress = (addr) => {
    const lookup = new SmartySDK.usStreet.Lookup();
    lookup.street = addr.street1;
    lookup.street2 = addr.street2;
    lookup.city = addr.city;
    lookup.state = addr.stateOrProvince;
    lookup.zipCode = addr.postalCode;
    lookup.match = 'enhanced';
    if (lookup.street) {
      usStreetClient
        .send(lookup)
        .then(response => updateStateFromValidatedUsAddress(response, true, addr))
        .catch(() => {
          setErrorMessage('Not a valid address');
          setAddress({ ...addr, isValid: false });
        });
    } else {
      setErrorMessage('Not a valid address');
      setAddress({ ...addr, isValid: false });
    }
  };

  const formatAutocompleteSuggestion = ({
    streetLine,
    secondary,
    // entries,
    city,
    state,
    zipcode = '',
  } = {}) => {
    const street = streetLine ? `${streetLine} ` : '';
    const additional = secondary
      ? `${secondary} `
      : '';
    // const entry = entries !== 0 ? ` ${entries} ` : '';
    const cite = city ? `${city} ` : '';
    const stateOrProvince = state ? `${state}, ` : '';

    return `${street}${additional}${cite}${stateOrProvince}${zipcode}`;
  };

  const queryAutocompleteForSuggestions = (addr) => {
    if (addr.street1) {
      const lookup = new AutocompleteLookup(addr.street1);
      lookup.source = 'all';
      setSuggestions([]);
      autoCompleteClient
        .send(lookup)
        .then((results) => {
          setSuggestions(results.result);
          clearTimeout(lookupTimeout);
          setLookupTimeout(null);
          const hasMatches = (results.result || []).find((s) => addr.street1 === `${s.streetLine}${s.secondary ? ` ${s.secondary}` : ''}`);
          if (!results?.result?.length || !hasMatches) {
            clearTimeout(validateTimeout);
            setValidateTimeout(setTimeout(() => {
              validateUsAddress(addr);
            }, 1000));
          }
        })
        .catch((e) => {
          clearTimeout(lookupTimeout);
          setLookupTimeout(null);
          clearTimeout(validateTimeout);
          setValidateTimeout(setTimeout(() => {
            validateUsAddress(addr);
          }, 1000));
          // eslint-disable-next-line no-console
          console.error(e.message);
        });
    } else {
      setSuggestions([]);
    }
  };

  const useAutoCompleteSuggestion = (event, sug) => {
    if (sug) {
      const updated = {
        street1: `${sug.streetLine}${sug.secondary ? ` ${sug.secondary}` : ''}`,
        street2: '',
        city: sug.city,
        stateOrProvince: sug.state,
        postalCode: sug.zipcode,
        postalAdditional: sug.postalAdditional || '',
        cityLat: sug.lat || '',
        cityLong: sug.lng || '',
        addressLat: sug.lat || '',
        addressLong: sug.lng || '',
        countryId: defaultCountryId,
      };
      setAddress(updated);
      setTouched(false);
      validateUsAddress(updated);
    } else {
      const data = {
        street1: '',
        street2: '',
        city: '',
        stateOrProvince: '',
        postalCode: '',
        postalAdditional: '',
        cityLat: '',
        cityLong: '',
        addressLat: '',
        addressLong: '',
        countryId: defaultCountryId,
      };
      setAddress(data);
      setTouched(true);
      validateUsAddress(data);
      onChange(data);
    }
    setSuggestions([]);
  };

  useEffect(() => {
    if (values) {
      setAddress((c) => ({
        ...c,
        street1: values.street1,
        street2: values.street2,
        city: values.city,
        stateOrProvince: values.stateOrProvince,
        postalCode: values.postalCode,
        postalAdditional: values.postalAdditional,
        countryId: values.countryId || defaultCountryId,
        isValid: values.isValid,
        cityLat: values.cityLatitude,
        cityLong: values.cityLongitude,
        addressLat: values.latitude,
        addressLong: values.longitude,
      }));
      // Disabling address validation on load for optimization and avoid validating already valid address.
      // if (values.street1) {
      //   validateUsAddress(values);
      // }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFieldChange = (field, value) => {
    if (address[field] !== value) {
      const addre = {
        ...address,
        [field]: value,
        isValid: false,

      };
      setAddress(addre);
      clearTimeout(validateTimeout);
      if (field === 'street1') {
        clearTimeout(lookupTimeout);
        setLookupTimeout(setTimeout(() => {
          queryAutocompleteForSuggestions(addre);
        }, 500));
      } else {
        setValidateTimeout(setTimeout(() => {
          validateUsAddress(addre);
        }, 1000));
      }
    }
  };

  useEffect(() => {
    onChange(address);
    // TODO: Need to add onChange dep if required
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);
  return (
    <div>
      <Grid container spacing={3}>
        <Typography color='error' style={{ margin: '10px 0 0 16px' }}>{errorMessage}</Typography>
        {suggestion && (
          <Grid
            container
            spacing={3}
            xs={12}
            style={{ marginLeft: 10, marginRight: 10, border: '1px solid green', marginTop: 10 }}
          >
            <Grid
              item
              lg={validationAnalysis.length ? 7 : 12}
              md={12}
              xs={12}
            >
              <Typography className={classes.suggestionTitle}>
                Based on your input below address is suggested with corrections,
              </Typography>
              <Typography className={classes.suggestion}>
                {`${suggestion.street1}${suggestion.street2
                  ? ` ${suggestion.street2}`
                  : ''
                  } ${suggestion.city} ${suggestion.stateOrProvince
                  }, ${suggestion.postalCode}${suggestion.postalAdditional
                    ? `-${suggestion.postalAdditional}`
                    : ''
                  }`}
              </Typography>
            </Grid>
            {validationAnalysis.length ? (
              <Grid item lg={5} md={12} xs={12}>
                <Typography
                  className={classes.suggestionAnalysis}
                >
                  <ul style={{ textAlign: 'left' }}>
                    {validationAnalysis.map((a) => (
                      <li>{a}</li>
                    ))}
                  </ul>
                </Typography>
              </Grid>
            ) : (
              ''
            )}
            <Grid
              item
              xs={12}
              style={{ textAlign: 'center' }}
              spacing={3}
            >
              {/* <Button
                variant="contained"
                style={{ marginRight: 5 }}
                color="secondary"
                onClick={() => setSuggestion(null)}
              >
                Ignore
              </Button> */}
              <Button
                variant='contained'
                color='primary'
                onClick={() => {
                  setSuggestion(null);
                  setAddress(suggestion);
                }}
              >
                Confirm
              </Button>
            </Grid>
          </Grid>
        )}
        <Grid item xs={12}>
          <Autocomplete
            autoComplete
            options={suggestions}
            inputValue={address.street1}
            defaultValue={address.street1}
            onChange={useAutoCompleteSuggestion}
            onInputChange={(event) => {
              if (event?.target) {
                setErrorMessage('');
                handleFieldChange('street1', event.target.value);
              }
            }}
            fullWidth
            autoHighlight
            getOptionLabel={(option) => `${option.streetLine}${option.secondary ? ` ${option.secondary}` : ''}`}
            renderOption={(sug, option) => (
              <Box component='li' sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...sug}>
                <LocationOnIcon />
                {formatAutocompleteSuggestion(option)}
              </Box>
            )}
            renderInput={params => (
              <TextField
                autoFocus
                {...params}
                margin='normal'
                placeholder='Search Address'
                fullWidth
                variant={variant}
                label={streetLabel}
                error={touched}
                helperText={touched && 'Street is required'}
                InputProps={{
                  ...params.InputProps,
                  inputProps: {
                    ...params.inputProps,
                    autoComplete: 'street1',
                  },
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  classes: {
                    focused: 'focused',
                  },
                }}
              />
            )}
          />
        </Grid>
        {/* <Grid item xs={12}>
          <TextField
            fullWidth
            name='street2'
            label={additionalLabel}
            value={address.street2}
            InputLabelProps={{
              classes: {
                focused: 'focused',
              },
            }}
            onBlur={(e) => {
              e.persist();
              const data = {
                ...address,
                street2: e.target.value,
              };
              validateUsAddress(data);
            }}
            onChange={e => {
              e.persist();
              setAddress({
                ...address,
                street2: e.target.value,
              });
            }}
          />
        </Grid> */}
        <Grid item lg={6} md={6} xs={12}>
          <TextField
            label={cityLabel}
            fullWidth
            name='city'
            variant={variant}
            value={address.city}
            onChange={(e) => {
              handleFieldChange('city', e.target.value);
            }}
            InputLabelProps={{
              classes: {
                focused: 'focused',
              },
            }}
            error={touched}
            helperText={touched && 'City is required'}
          />
        </Grid>
        <Grid item lg={6} md={6} xs={12}>
          <FormControl fullWidth style={{ top: 5, textAlign: 'left' }}>
            <InputLabel style={{ color: touched && !address.stateOrProvince ? '#f44336' : '' }}>{stateLabel}</InputLabel>
            <Select
              name='stateOrProvince'
              variant={variant}
              value={address.stateOrProvince}
              onChange={(e) => {
                handleFieldChange('stateOrProvince', e.target.value);
              }}
              error={touched}
            >
              {STATE_LIST.map(s => (
                <MenuItem value={s.code}>{s.label}</MenuItem>
              ))}
            </Select>
            <span style={{ color: '#f44336', fontSize: 12, paddingTop: 5 }}>
              {touched && !address.stateOrProvince ? 'State is required' : ''}
            </span>
          </FormControl>
        </Grid>
        <Grid item lg={6} md={6} xs={12}>
          <TextField
            name='postalCode'
            fullWidth
            variant={variant}
            label={zipcodeLabel}
            value={address.postalCode}
            onChange={(e) => {
              if (e) {
                e.persist();
                handleFieldChange('postalCode', e.target.value);
              }
            }}
            InputLabelProps={{
              classes: {
                focused: 'focused',
              },
            }}
            error={touched}
            helperText={touched && 'Postal Code is required'}
          />
        </Grid>
        <Grid item lg={6} md={6} xs={12}>
          <FormControl fullWidth style={{ top: 5, textAlign: 'left' }}>
            <InputLabel>{countryLabel}</InputLabel>
            <Select
              variant={variant}
              name='country'
              label={countryLabel}
              fullWidth
              value={address.countryId}
            >
              {countryOptions.length ? countryOptions.map((c) => <MenuItem value={c.id}>{c.name}</MenuItem>) : ''}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </div>
  );
};

SmartyAddressField.propTypes = {
  onChange: PropTypes.func,
  values: PropTypes.object,
  streetLabel: PropTypes.string,
  additionalLabel: PropTypes.string,
  cityLabel: PropTypes.string,
  stateLabel: PropTypes.string,
  zipcodeLabel: PropTypes.string,
  countryLabel: PropTypes.string,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
  partial: PropTypes.bool,
  error: PropTypes.bool,
  AddressErrors: PropTypes.object,
  helpText: PropTypes.node,
  countries: PropTypes.array,
};

SmartyAddressField.defaultProps = {
  variant: 'standard',
  streetLabel: 'Street',
  additionalLabel: 'Apartment or Unit # (If Applicable)',
  cityLabel: 'City',
  stateLabel: 'State',
  zipcodeLabel: 'Postal Code',
  countryLabel: 'Country',
  partial: true,
  error: false,
  helpText: '',
};

export default SmartyAddressField;
